import { create } from "zustand";
import { debounce } from "lodash";
import axios from "axios";
import { v4 as uuid } from "uuid";
import { getCookie } from "../components/shared/Cookie";

type ProjectResourcesState = {
  token: string;
  files: { [key: string]: string };
  storeignore: string[];
  writeFile: (path: string, content: string) => void;
  readFile: (path: string) => string | undefined;
  deleteFile: (path: string) => void;
  listFiles: (path?: string) => string[];
  initResources: (
    token: string,
    files: { [key: string]: string },
    nextToken?: string
  ) => Promise<void>;
  debouncers: Record<string, any>;
  getDebouncer: (key: string, wait?: number) => any;
  axiosInstance?: any;
  accessTokenKey?: string;
};

export const useProjectResourcesStore = create<ProjectResourcesState>(
  (set, get) => ({
    token: uuid(),
    files: {},
    storeignore: [".blockly"],
    debouncers: {},
    getDebouncer: (key: string, wait = 5000) => {
      if (!get().debouncers[key]) {
        set((state) => ({
          debouncers: {
            ...state.debouncers,
            [key]: debounce(
              (path: string, content: string) => {
                // parital form data ile dosya yükleme
                const formData = new FormData();
                // file format file:
                const blob = new Blob([content], {
                  type: "application/octet-stream",
                });
                formData.append("file", blob, encodeURIComponent(path));
                formData.append("token", get().token);
                console.log(`Saving file "${path}" to project resources...`);
                get()
                  .axiosInstance?.post("/", formData, {
                    headers: {
                      Authorization: `Bearer ${getCookie(
                        get().accessTokenKey || ""
                      )}`,
                    },
                  })
                  .then(() => {
                    console.log(`File ${path} saved to project resources.`);
                  });
              },
              wait,
              {
                leading: false,
                trailing: true,
              }
            ),
          },
        }));
      }
      return get().debouncers[key];
    },
    initResources: async (token, files = {}, nextToken) => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_EDITOR_API_URL}/api/project-resources${
            nextToken ? `?continuationToken=${nextToken}` : ""
          }`,
          {
            headers: {
              Authorization: `Bearer ${getCookie(token)}`,
            },
          }
        );
        if (data.nextToken) {
          return get().initResources(
            token,
            { ...files, ...data.files },
            data.nextToken
          );
        } else {
          set({
            files: { ...files, ...data.files },
            axiosInstance: axios.create({
              baseURL: `${process.env.REACT_APP_EDITOR_API_URL}/api/project-resources`,
            }),
            accessTokenKey: token,
            storeignore: get().storeignore.concat(
              JSON.parse(files[".storeignore"] || "[]")
            ),
          });
        }
      } catch (error) {
        console.error("Invalid token. Please log in again.");
      }
    },
    writeFile: (path: string, content: string) => {
      set((state) => ({
        files: {
          ...state.files,
          [path]: content,
        },
      }));
      if (!get().storeignore.some((ignore) => path.includes(ignore))) {
        const debouncer = get().getDebouncer(path);
        debouncer(path, content);
      }
    },
    readFile: (path: string) => {
      return get().files[path];
    },
    deleteFile: (path: string) => {
      set((state) => {
        const updatedFiles = structuredClone(state.files);
        const files = [];
        for (const filePath in updatedFiles) {
          if (filePath.startsWith(path)) {
            files.push(filePath);
            delete updatedFiles[filePath];
          }
        }
        get()
          .axiosInstance?.delete("/", {
            data: { files },
            headers: {
              Authorization: `Bearer ${getCookie(get().accessTokenKey || "")}`,
            },
          })
          .then(() => {});
        return { files: updatedFiles };
      });
    },
    listFiles: (path?: string) => {
      return Object.keys(get().files)
        .filter((filePath) => filePath.startsWith(path || "/"))
        .map((filePath) => {
          filePath = filePath.replace(path + "/" || "/", "");
          return filePath;
        });
    },
  })
);
